import {
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import { useCallback, useMemo, useRef, useState } from 'react';
import { calculateFlowDurationFromSlots, computeAllSlotTimings } from './slot-timing';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { slotTimingPreviewStore } from './slot-timing-preview-store';
import { isSegmentedSession } from './session-segment.helpers';
import {
  isScheduleLiveTimedSlotLocked,
  isTimedReorderMoveAllowed,
  type ScheduleLiveTimedSlotLock,
} from '@/helpers/schedule-live-slot-lock.helper';

export type SlotListDndParams = {
  timedSlots: FlowSlotDto[];
  untimedSlots: FlowSlotDto[];
  timedSortableIds: string[];
  untimedSortableIds: string[];
  flow: FlowDto | undefined;
  syncFlow: (flow: FlowDto, options?: { omitEventSlotTiming?: boolean }) => void;
  setLocalSlots: (slots: FlowSlotDto[]) => void;
  setRefreshing: (value: boolean) => void;
  setIsDragOptimistic: (value: boolean) => void;
  setIsDragging: (value: boolean) => void;
  setActiveId: (id: string | null) => void;
  TIMED_CONTAINER_ID: string;
  UNTIMED_CONTAINER_ID: string;
  /** Schedule tab while live: blocks reorder into the playing-prefix of timed slots */
  scheduleLiveTimedSlotLock?: ScheduleLiveTimedSlotLock;
};

export type SlotListDndReturn = {
  sensors: ReturnType<typeof useSensors>;
  onDragStart: (event: DragStartEvent) => void;
  onDragOver: (event: DragOverEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onDragCancel: () => void;
  dragTimedIds: string[] | null;
  dragUntimedIds: string[] | null;
};

const isUntimedSlot = (slot: FlowSlotDto) =>
  slot.isUntimed ?? (slot.plannedOffsetMinutes === undefined && slot.computedOffsetMinutes === undefined);
const areIdsEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

export function useSlotListDnd({
  timedSlots,
  untimedSlots,
  timedSortableIds,
  untimedSortableIds,
  flow,
  syncFlow,
  setLocalSlots,
  setRefreshing,
  setIsDragOptimistic,
  setIsDragging,
  setActiveId,
  TIMED_CONTAINER_ID,
  UNTIMED_CONTAINER_ID,
  scheduleLiveTimedSlotLock,
}: SlotListDndParams): SlotListDndReturn {
  const debug = useCallback((message: string, data?: Record<string, unknown>) => {
    // Temporary drag/drop diagnostics (remove after issue is fixed).
    if (import.meta.env.DEV) {
      console.debug(`[slot-dnd] ${message}`, data ?? {});
    }
  }, []);
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const lastOverKeyRef = useRef<string | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const pendingPreviewRef = useRef<FlowSlotDto[] | null>(null);
  const [dragTimedIds, setDragTimedIds] = useState<string[] | null>(null);
  const [dragUntimedIds, setDragUntimedIds] = useState<string[] | null>(null);
  const slotsById = useMemo(
    () => new Map([...timedSlots, ...untimedSlots].map((slot) => [slot.id, slot])),
    [timedSlots, untimedSlots],
  );

  const scheduleTimingPreview = useCallback(
    (nextTimedSlots: FlowSlotDto[] | null) => {
      pendingPreviewRef.current = nextTimedSlots;
      if (rafIdRef.current !== null) return;
      rafIdRef.current = window.requestAnimationFrame(() => {
        const pending = pendingPreviewRef.current;
        pendingPreviewRef.current = null;
        rafIdRef.current = null;
        if (!pending) {
          slotTimingPreviewStore.clear();
          return;
        }
        const durationToUse =
          flow?.durationPlannedMinutes !== undefined && flow.durationPlannedMinutes > 0
            ? flow.durationPlannedMinutes
            : undefined;
        const recalculated = computeAllSlotTimings(pending, durationToUse);
        const previewMap = new Map<string, { offsetMinutes?: number; durationMinutes?: number }>();
        recalculated.forEach((slot) => {
          previewMap.set(slot.id, {
            offsetMinutes: slot.computedOffsetMinutes,
            durationMinutes: slot.computedDurationMinutes,
          });
        });
        slotTimingPreviewStore.setSnapshot(previewMap);
      });
    },
    [flow],
  );
  const applyTimingPreview = useCallback((nextTimedSlots: FlowSlotDto[]) => {
    const previewMap = new Map<string, { offsetMinutes?: number; durationMinutes?: number }>();
    nextTimedSlots.forEach((slot) => {
      previewMap.set(slot.id, {
        offsetMinutes: slot.computedOffsetMinutes,
        durationMinutes: slot.computedDurationMinutes,
      });
    });
    slotTimingPreviewStore.setSnapshot(previewMap);
  }, []);
  const resolveOverId = useCallback((overId: string | null) => {
    if (!overId) return null;
    return overId;
  }, []);
  const resolveContainerId = useCallback(
    (id: string | null, data: unknown) => {
      if (!id) return null;
      if (id === TIMED_CONTAINER_ID || id === UNTIMED_CONTAINER_ID) return id;
      const containerId = (data as { current?: { containerId?: string } })?.current?.containerId;
      return typeof containerId === 'string' ? containerId : null;
    },
    [TIMED_CONTAINER_ID, UNTIMED_CONTAINER_ID],
  );
  const buildProjectedTimedSlots = useCallback(
    (nextTimedIds: string[], activeId: string, activeSlot: FlowSlotDto, targetContainerId: string | null) => {
      return nextTimedIds
        .map((id) => {
          const slot = slotsById.get(id);
          if (!slot) return null;
          if (id === activeId && isUntimedSlot(activeSlot) && targetContainerId === TIMED_CONTAINER_ID) {
            return {
              ...slot,
              isUntimed: false,
              plannedOffsetMinutes: undefined,
              computedOffsetMinutes: undefined,
              plannedDurationMinutes:
                slot.plannedDurationMinutes ??
                slot.content?.plannedDurationMinutes ??
                slot.computedDurationMinutes ??
                0,
            };
          }
          return slot;
        })
        .filter((slot): slot is FlowSlotDto => Boolean(slot));
    },
    [slotsById, TIMED_CONTAINER_ID],
  );
  const onDragStart = (event: DragStartEvent) => {
    const activeId = String(event.active.id);
    if (isScheduleLiveTimedSlotLocked(activeId, scheduleLiveTimedSlotLock)) {
      return;
    }
    setIsDragging(true);
    setActiveId(activeId);
    setDragTimedIds(timedSortableIds);
    setDragUntimedIds(untimedSortableIds);
    slotTimingPreviewStore.clear();
    debug('drag-start', { activeId: String(event.active.id) });
  };

  const onDragCancel = () => {
    setIsDragging(false);
    setActiveId(null);
    setDragTimedIds(null);
    setDragUntimedIds(null);
    lastOverKeyRef.current = null;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    pendingPreviewRef.current = null;
    setRefreshing(false);
    debug('drag-cancel');
  };

  const onDragOver = useCallback(
    (event: DragOverEvent) => {
      const rawOverId = event.over?.id ? String(event.over.id) : null;
      const overId = resolveOverId(rawOverId);
      const activeId = event.active?.id ? String(event.active.id) : null;
      if (!overId || !activeId) return;
      const overKey = `${activeId}:${overId}`;
      if (lastOverKeyRef.current === overKey) {
        return;
      }
      lastOverKeyRef.current = overKey;
      debug('drag-over', { activeId, overId });

      const activeSlot = slotsById.get(activeId) ?? null;
      if (!activeSlot) return;

      const activeContainerId =
        resolveContainerId(activeId, event.active.data) ??
        (isUntimedSlot(activeSlot) ? UNTIMED_CONTAINER_ID : TIMED_CONTAINER_ID);
      const overContainerId =
        resolveContainerId(overId, event.over?.data) ??
        (overId === TIMED_CONTAINER_ID || overId === UNTIMED_CONTAINER_ID ? overId : null);

      if (!overContainerId) return;

      if (
        isSegmentedSession(flow, activeSlot) &&
        activeContainerId === TIMED_CONTAINER_ID &&
        overContainerId === UNTIMED_CONTAINER_ID
      ) {
        return;
      }

      const currentTimedIds = dragTimedIds ?? timedSortableIds;
      const currentUntimedIds = dragUntimedIds ?? untimedSortableIds;
      let nextTimedIds = currentTimedIds;
      let nextUntimedIds = currentUntimedIds;
      if (activeContainerId === overContainerId) {
        if (overContainerId === TIMED_CONTAINER_ID) {
          const fromIndex = currentTimedIds.indexOf(activeId);
          const lastIndex = currentTimedIds.length - 1;
          const lastItemId = lastIndex >= 0 ? currentTimedIds[lastIndex] : null;
          const rawToIndex = overId === TIMED_CONTAINER_ID ? currentTimedIds.length : currentTimedIds.indexOf(overId);
          const toIndex = rawToIndex === -1 ? currentTimedIds.length : rawToIndex;
          const candidateTimedIds =
            fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex
              ? arrayMove(currentTimedIds, fromIndex, toIndex)
              : null;
          if (
            candidateTimedIds &&
            isTimedReorderMoveAllowed(activeId, candidateTimedIds, scheduleLiveTimedSlotLock) &&
            !(overId === TIMED_CONTAINER_ID && fromIndex === lastIndex) &&
            !(overId === lastItemId && fromIndex === lastIndex)
          ) {
            nextTimedIds = candidateTimedIds;
          }
        } else {
          const fromIndex = currentUntimedIds.indexOf(activeId);
          const lastIndex = currentUntimedIds.length - 1;
          const lastItemId = lastIndex >= 0 ? currentUntimedIds[lastIndex] : null;
          const rawToIndex =
            overId === UNTIMED_CONTAINER_ID ? currentUntimedIds.length : currentUntimedIds.indexOf(overId);
          const toIndex = rawToIndex === -1 ? currentUntimedIds.length : rawToIndex;
          if (
            fromIndex !== -1 &&
            toIndex !== -1 &&
            fromIndex !== toIndex &&
            !(overId === UNTIMED_CONTAINER_ID && fromIndex === lastIndex) &&
            !(overId === lastItemId && fromIndex === lastIndex)
          ) {
            nextUntimedIds = arrayMove(currentUntimedIds, fromIndex, toIndex);
          }
        }
      } else {
        const baseTimedIds = currentTimedIds.filter((id) => id !== activeId);
        const baseUntimedIds = currentUntimedIds.filter((id) => id !== activeId);
        if (activeContainerId === TIMED_CONTAINER_ID) {
          const targetIndex = overId === UNTIMED_CONTAINER_ID ? baseUntimedIds.length : baseUntimedIds.indexOf(overId);
          const insertAt = targetIndex === -1 ? baseUntimedIds.length : targetIndex;
          nextTimedIds = baseTimedIds;
          nextUntimedIds = [...baseUntimedIds.slice(0, insertAt), activeId, ...baseUntimedIds.slice(insertAt)];
        } else {
          const targetIndex = overId === TIMED_CONTAINER_ID ? baseTimedIds.length : baseTimedIds.indexOf(overId);
          const insertAt = targetIndex === -1 ? baseTimedIds.length : targetIndex;
          const candidateTimedIds = [...baseTimedIds.slice(0, insertAt), activeId, ...baseTimedIds.slice(insertAt)];
          if (!isTimedReorderMoveAllowed(activeId, candidateTimedIds, scheduleLiveTimedSlotLock)) {
            return;
          }
          nextUntimedIds = baseUntimedIds;
          nextTimedIds = candidateTimedIds;
        }
      }

      const timedChanged = !areIdsEqual(nextTimedIds, currentTimedIds);
      const untimedChanged = !areIdsEqual(nextUntimedIds, currentUntimedIds);
      if (timedChanged) {
        setDragTimedIds(nextTimedIds);
      }
      if (untimedChanged) {
        setDragUntimedIds(nextUntimedIds);
      }
      if (!timedChanged && !untimedChanged) {
        return;
      }

      if (activeContainerId === TIMED_CONTAINER_ID || overContainerId === TIMED_CONTAINER_ID) {
        const projectedTimed = buildProjectedTimedSlots(nextTimedIds, activeId, activeSlot, overContainerId);
        scheduleTimingPreview(projectedTimed);
      } else {
        scheduleTimingPreview(null);
      }
    },
    [
      slotsById,
      dragTimedIds,
      dragUntimedIds,
      timedSortableIds,
      untimedSortableIds,
      flow,
      TIMED_CONTAINER_ID,
      UNTIMED_CONTAINER_ID,
      scheduleTimingPreview,
      resolveOverId,
      resolveContainerId,
      buildProjectedTimedSlots,
      scheduleLiveTimedSlotLock,
    ],
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const rawDropTargetId = over?.id ? String(over.id) : null;
    const dropTargetId = resolveOverId(rawDropTargetId);
    const activeId = String(active.id);
    const activeSlot = slotsById.get(activeId) ?? null;
    setIsDragging(false);
    setActiveId(null);
    slotTimingPreviewStore.clear();
    setDragTimedIds(null);
    setDragUntimedIds(null);
    lastOverKeyRef.current = null;
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    pendingPreviewRef.current = null;
    debug('drag-end', {
      activeId,
      rawDropTargetId,
      dropTargetId,
    });

    if (!flow || !over || !dropTargetId || !activeSlot) {
      setRefreshing(false);
      debug('drag-end-missing-flow-or-over', {
        hasFlow: Boolean(flow),
        hasOver: Boolean(over),
        hasDropTarget: Boolean(dropTargetId),
        hasActiveSlot: Boolean(activeSlot),
      });
      return;
    }

    const overContainerId =
      resolveContainerId(dropTargetId, over.data) ??
      (dropTargetId === TIMED_CONTAINER_ID || dropTargetId === UNTIMED_CONTAINER_ID ? dropTargetId : null);

    if (!overContainerId) {
      setRefreshing(false);
      debug('drag-end-missing-over-container', { dropTargetId });
      return;
    }

    const activeContainerId =
      resolveContainerId(activeId, active.data) ??
      (isUntimedSlot(activeSlot) ? UNTIMED_CONTAINER_ID : TIMED_CONTAINER_ID);

    if (
      isSegmentedSession(flow, activeSlot) &&
      activeContainerId === TIMED_CONTAINER_ID &&
      overContainerId === UNTIMED_CONTAINER_ID
    ) {
      setRefreshing(false);
      debug('drag-end-blocked-segmented-to-untimed', { activeId });
      return;
    }

    const finalTimedIds = Array.from(new Set(dragTimedIds ?? timedSortableIds));
    const finalUntimedIds = Array.from(new Set(dragUntimedIds ?? untimedSortableIds));
    if (!isTimedReorderMoveAllowed(activeId, finalTimedIds, scheduleLiveTimedSlotLock)) {
      setRefreshing(false);
      debug('drag-end-blocked-live-slot-lock', { activeId });
      return;
    }

    setRefreshing(true);

    const isActiveUntimed = isUntimedSlot(activeSlot);

    const finalTimedSlots = finalTimedIds
      .map((id) => {
        const slot = slotsById.get(id);
        if (!slot) return null;
        if (id === activeId && isActiveUntimed && overContainerId === TIMED_CONTAINER_ID) {
          return {
            ...slot,
            isUntimed: false,
            plannedOffsetMinutes: undefined,
            computedOffsetMinutes: undefined,
            plannedDurationMinutes:
              slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes ?? slot.computedDurationMinutes ?? 0,
          };
        }
        return slot;
      })
      .filter((slot): slot is FlowSlotDto => Boolean(slot));

    const finalUntimedSlots = finalUntimedIds
      .map((id) => {
        const slot = slotsById.get(id);
        if (!slot) return null;
        if (id === activeId && !isActiveUntimed && overContainerId === UNTIMED_CONTAINER_ID) {
          return {
            ...slot,
            plannedDurationMinutes: slot.plannedDurationMinutes ?? slot.content?.plannedDurationMinutes ?? 0,
            plannedOffsetMinutes: undefined,
            computedOffsetMinutes: undefined,
            isUntimed: true,
          };
        }
        return slot;
      })
      .filter((slot): slot is FlowSlotDto => Boolean(slot));

    const durationToUse = flow?.durationPlannedMinutes ?? calculateFlowDurationFromSlots(finalTimedSlots);
    const recalculatedTimedSlots = computeAllSlotTimings(finalTimedSlots, durationToUse);
    applyTimingPreview(recalculatedTimedSlots);

    const allSlots = [...recalculatedTimedSlots, ...finalUntimedSlots];
    setIsDragOptimistic(true);
    setLocalSlots(allSlots);
    syncFlow({ ...flow, slots: allSlots }, { omitEventSlotTiming: true });
    setRefreshing(false);
    debug('drag-end-commit', { dropTargetId });
  };

  return { sensors, onDragStart, onDragOver, onDragEnd, onDragCancel, dragTimedIds, dragUntimedIds };
}
